import { useState } from 'react';
import { Button, Tabs, Typography } from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import { MostHistoryList } from './history/list';
import { useTable } from 'src/hooks/use-table';
import {
  Actions,
  AppArea,
  MostFavouritesDocument,
  MostRequestHistoryStatus,
  MostScheduledJobsDocument,
  SortingDirection,
  Subjects,
} from 'src/graphql/generated/graphql';
import { MostFilter } from './filter/filter';
import { MostFavouriteList } from './favourite/list';
import { MostScheduledJobList } from 'src/components/most/list/scheduled-jobs';
import { ActionBar } from './action-bar/action-bar';
import { ActionBarWrapper } from 'src/components/common/wrapper/action-bar/wrapper';
import { AddToFavorites } from '../common/add-to-favorites';
import { RunSchedule } from '../common/run-schedule';
import { Schedule } from '@otp/icons';
import clsx from 'clsx';
import { Can } from 'src/utils/can';
import { useCan } from 'src/utils/can';

export enum TabType {
  History = 'history',
  Favourite = 'favourite',
  Scheduled = 'scheduled',
}

export enum MostFilterResultType {
  Alerts = 'alerts',
  Recent = 'recent',
  All = 'all',
}

export type MostFilterType = {
  search?: string;
  userId?: string;
  status?: MostRequestHistoryStatus;
  storeIds?: string[];
  results?: MostFilterResultType;
};

export const MostList = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'most.list',
  });

  const [activeTab, setActiveTab] = useState(TabType.History);
  const { can } = useCan();

  const tabs = [];

  if (
    can({
      action: Actions.Read,
      appArea: AppArea.Most,
      subject: Subjects.History,
    })
  ) {
    tabs.push({ label: t('tabs.history'), value: TabType.History });
  }

  if (
    can({
      action: Actions.Read,
      appArea: AppArea.Most,
      subject: Subjects.History,
    })
  ) {
    tabs.push({ label: t('tabs.favourite'), value: TabType.Favourite });
  }

  if (
    can({
      action: Actions.Read,
      appArea: AppArea.Most,
      subject: Subjects.Jobs,
    })
  ) {
    tabs.push({ label: t('tabs.scheduled'), value: TabType.Scheduled });
  }

  const tabExtraButtonMap = {
    [TabType.History]: null,
    [TabType.Favourite]: (
      <AddToFavorites refetchQueries={[MostFavouritesDocument]}>
        <Button variant="contained">{t('favourite.form.submit')}</Button>
      </AddToFavorites>
    ),
    [TabType.Scheduled]: (
      <RunSchedule
        title={
          (
            <div className="flex">
              <Schedule className={clsx('mr-2')} />
              <Typography variant="subtitle1">
                {t('schedule.form.title')}
              </Typography>
            </div>
          ) as any
        }
        refetchQueries={[MostScheduledJobsDocument]}
      >
        <Button variant="contained">{t('schedule.form.create')}</Button>
      </RunSchedule>
    ),
  };

  const { filter, onFilterChange, defaultFilter } = useTable<
    MostFilterType,
    Record<string, SortingDirection>
  >({
    defaultFilter: {
      results: MostFilterResultType.Recent,
    },
  });

  return (
    <Can I={Actions.Read} a={Subjects.Most} appArea={AppArea.Most}>
      <ActionBarWrapper>
        <ActionBar />
      </ActionBarWrapper>
      <ContentWrapper>
        <TableWrapper>
          <MostFilter
            onSubmit={onFilterChange}
            defaultFilter={defaultFilter}
            activeTab={activeTab}
          />
          <Tabs
            className="mb-2 mt-4"
            tabs={tabs}
            onChange={(_, value) => setActiveTab(value)}
            value={activeTab}
            extra={tabExtraButtonMap[activeTab]}
          />
          {activeTab === TabType.History && (
            <MostHistoryList
              filter={{
                search: filter.search,
                executedByUserId: filter.userId,
                storeIds: filter.storeIds || [],
                status: filter.status as string,
                onlyRecent: filter.results === MostFilterResultType.Recent,
                alertsOnly: filter.results === MostFilterResultType.Alerts,
              }}
            />
          )}
          {activeTab === TabType.Favourite && (
            <MostFavouriteList
              filter={{
                search: filter.search,
                storeIds: filter.storeIds,
              }}
            />
          )}
          {activeTab === TabType.Scheduled && (
            <MostScheduledJobList filter={filter} />
          )}
        </TableWrapper>
      </ContentWrapper>
    </Can>
  );
};
